import React, { useEffect, useRef, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateFormField } from "../../../../store/schedulingReducer";
import useStudentList from "../../../hooks/useStudentList";
import SvgMapper from "../../../features/common/SvgMapper";
import "../../Style.css";

const SEARCH_DEBOUNCE_MS = 350;

const SelectStudent = () => {
  const dispatch = useDispatch();
  const { studentName, isModalOpen } = useSelector((state) => state.scheduling);
  const { students, hasMore, loadMore, loading, fetchStudentList } = useStudentList();

  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const hasFetchedInitial = useRef(false);
  const searchDebounceRef = useRef(null);
  const fetchStudentListRef = useRef(fetchStudentList);
  const wasDropdownOpenRef = useRef(false);
  fetchStudentListRef.current = fetchStudentList;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isModalOpen && (!students || students.length === 0) && !loading && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchStudentListRef.current(1, false, {}, true);
    }
  }, [isModalOpen, students, loading]);

  useEffect(() => {
    if (!isDropdownOpen) {
      wasDropdownOpenRef.current = false;
      return;
    }
    const justOpened = !wasDropdownOpenRef.current;
    wasDropdownOpenRef.current = true;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (justOpened && !searchQuery.trim()) return;

    const query = searchQuery.trim();
    const timer = setTimeout(() => {
      searchDebounceRef.current = null;

      let filters = {};

      if (query) {
        filters = query.includes("@") ? { email: query } : { name: query };
      }

      fetchStudentListRef.current(1, false, filters);
    }, SEARCH_DEBOUNCE_MS);
    searchDebounceRef.current = timer;
    return () => {
      clearTimeout(timer);
      if (searchDebounceRef.current === timer) {
        searchDebounceRef.current = null;
      }
    };
  }, [searchQuery, isDropdownOpen]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setIsDropdownOpen(false);
    }
  }, [isModalOpen]);

  const handleScroll = useCallback(() => {
    const currentRef = scrollRef.current;
    if (!currentRef) return;

    const { scrollTop, clientHeight, scrollHeight } = currentRef;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    const dropdownListEl = scrollRef.current;
    if (isDropdownOpen && dropdownListEl) {
      dropdownListEl.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (dropdownListEl) {
        dropdownListEl.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isDropdownOpen, handleScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleStudentSelect = (student) => {
    const studentId = student.id || student.student_id;
    dispatch(updateFormField({ key: "studentName", value: student.name }));
    dispatch(updateFormField({ key: "studentId", value: studentId }));
    dispatch(updateFormField({ key: "studentEmail", value: student.email }));
    setIsDropdownOpen(false);
  };

  const listStudents = Array.isArray(students) ? students: [];

  return (
    <div className="select-student-container">
      <label className="select-student-label">
        Select Student
      </label>

      <div className="select-student-wrapper" ref={dropdownRef}>
        <div
          className={`select-student-dropdown ${isDropdownOpen ? "select-student-dropdown-open" : ""}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {SvgMapper.dropdown(
            `select-student-dropdown-icon ${isDropdownOpen ? "rotated" : ""}`
          )}
          {isDropdownOpen ? (
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="select-student-search-inline"
            />
          ) : (
            <span className="select-student-dropdown-text">
              {studentName || "Choose a student..."}
            </span>
          )}
        </div>

        {isDropdownOpen && (
          <div className="select-student-dropdown-panel">
            <div
              ref={scrollRef}
              className="select-student-options"
            >
              {listStudents.map((student) => {
                const name = student?.name || "N/A";
                const email = student?.email || "";
                return (
                  <div
                    key={student?.id || student?.student_id || name}
                    className="select-student-option"
                    onClick={() => handleStudentSelect(student)}
                  >
                    <div className="select-student-option-text">
                      {name}{email ? ` (${email})` : ""}
                    </div>
                  </div>
                );
              })}
              {listStudents.length === 0 && !loading && (
                <div className="select-student-no-more">
                  {searchQuery.trim() ? "No students match your search" : "No students"}
                </div>
              )}
              {loading && (
                <div className="select-student-loading">
                  Loading more...
                </div>
              )}
              {!hasMore && !loading && listStudents.length > 0 && (
                <div className="select-student-no-more">
                  No more students
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectStudent;